import type { EspaceOverview, EspaceRequest } from "@shared/espace.types";
import { Briefcase } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ds/Badge";
import { Button } from "@/components/ds/Button";
import { Card, CardHeader } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { FilterChip } from "@/components/ds/FilterChip";
import { formatDate, REQUEST_STATUS, relativeDate } from "./labels";

/**
 * « Mes offres » : la liste, et le détail dans la même page.
 *
 * Les filtres sont des ONGLETS de statut et non un menu déroulant : à quatre
 * valeurs, un menu cache l'information qu'il devrait donner — combien il y en a
 * dans chaque état.
 */

const FILTERS = [
	{ key: "all", label: "Toutes" },
	{ key: "nouveau", label: "Reçues" },
	{ key: "a_completer", label: "À compléter" },
	{ key: "validee", label: "Publiées" },
	{ key: "archivee", label: "Terminées" },
	{ key: "refusee", label: "Non retenues" },
] as const;

export function RequestsPage({
	overview,
	onDeposit,
}: {
	overview: EspaceOverview;
	onDeposit: () => void;
}) {
	const [filter, setFilter] = useState<string>("all");
	const shown =
		filter === "all"
			? overview.requests
			: overview.requests.filter((request) => request.status === filter);

	if (overview.requests.length === 0) {
		return (
			<div className="flex flex-col gap-5">
				<h1 className="t-h1 text-mg-heading">Mes offres</h1>
				<Card padded={false}>
					<EmptyState
						icon={<Briefcase className="size-5" aria-hidden="true" />}
						title="Aucune offre déposée"
						body="Collez l'annonce que vous avez déjà, vérifiez les champs, et votre école la reçoit aussitôt."
						action={
							<Button tone="primary" onClick={onDeposit}>
								Déposer une offre
							</Button>
						}
					/>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="t-h1 text-mg-heading">Mes offres</h1>
					<p className="t-body mt-1 text-mg-secondary">
						{overview.requests.length === 1
							? "1 offre déposée"
							: `${overview.requests.length} offres déposées`}
					</p>
				</div>
				<Button tone="primary" onClick={onDeposit}>
					Déposer une offre
				</Button>
			</header>

			<div className="flex flex-wrap gap-2">
				{FILTERS.map((entry) => {
					const count =
						entry.key === "all"
							? overview.requests.length
							: overview.requests.filter((request) => request.status === entry.key).length;
					if (count === 0 && entry.key !== "all") return null;
					return (
						<FilterChip
							key={entry.key}
							selected={filter === entry.key}
							count={count}
							onClick={() => setFilter(entry.key)}
						>
							{entry.label}
						</FilterChip>
					);
				})}
			</div>

			<ul className="flex flex-col gap-3">
				{shown.map((request) => (
					<li key={request.id}>
						<RequestCard request={request} />
					</li>
				))}
			</ul>
		</div>
	);
}

function RequestCard({ request }: { request: EspaceRequest }) {
	const status = REQUEST_STATUS[request.status];
	return (
		<Link
			to={`/espace/offres/${request.id}`}
			className="block rounded-card border border-mg-border bg-mg-surface p-5 transition-shadow hover:shadow-card"
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="t-h4 text-mg-heading">{request.job}</p>
					<p className="t-body mt-1 text-mg-secondary">
						{[request.schoolName, request.location, request.contract].filter(Boolean).join(" · ")}
					</p>
				</div>
				<Badge tone={status?.tone ?? "neutral"}>{status?.label ?? request.status}</Badge>
			</div>
			<p className="t-caption mt-4 border-t border-mg-border-subtle pt-3 text-mg-secondary">
				Déposée {relativeDate(request.createdAt)}
				{status?.help ? ` · ${status.help}` : ""}
			</p>
		</Link>
	);
}

/** Le détail d'une offre : ce qui a été déposé, et ce que l'école en a fait. */
export function RequestDetailPage({
	overview,
	onRedeposer,
}: {
	overview: EspaceOverview;
	/** Rend l'id du redépôt, ou `null` si l'offre n'appartient pas à ce contact. */
	onRedeposer: (requestId: string) => Promise<string | null>;
}) {
	const { id } = useParams();
	const navigate = useNavigate();
	const [busy, setBusy] = useState(false);
	const [echec, setEchec] = useState(false);
	const request = overview.requests.find((candidate) => candidate.id === id);
	const selections = overview.selections.filter(
		(selection) => selection.offerRequestId === id && selection.profiles.length > 0,
	);

	async function redeposer() {
		if (!id) return;
		setBusy(true);
		setEchec(false);
		const nouvelle = await onRedeposer(id);
		setBusy(false);
		// On EMMÈNE l'entreprise sur la copie. La laisser sur l'ancienne, avec un
		// message de succès, lui ferait chercher où est passée sa nouvelle offre.
		if (nouvelle) navigate(`/espace/offres/${nouvelle}`);
		// Et quand ça échoue, on le DIT. Un bouton qui repasse de « Redépôt… » à
		// « Redéposer » sans un mot laisse l'entreprise devant un geste qui n'a rien
		// produit, sans savoir s'il faut recommencer.
		else setEchec(true);
	}

	if (!request) {
		return (
			<Card padded={false}>
				<EmptyState
					title="Cette offre n'existe plus"
					body="Elle a peut-être été déposée avec une autre adresse email."
					action={
						<Link to="/espace/offres" className="t-label text-mg-action hover:underline">
							Revenir à mes offres
						</Link>
					}
				/>
			</Card>
		);
	}

	const status = REQUEST_STATUS[request.status];
	const facts: [string, string | null][] = [
		["École", request.schoolName],
		["Ville", request.location],
		["Contrat", request.contract],
		["Télétravail", request.telework],
		["Niveau visé", request.qualification],
		["Démarrage", request.startsOn ? formatDate(request.startsOn) : null],
		["Postes", request.headcount ? String(request.headcount) : null],
		["Date limite", request.expiresOn ? formatDate(request.expiresOn) : null],
		[
			"Formations visées",
			request.targetPrograms.length > 0 ? request.targetPrograms.join(", ") : null,
		],
	];

	return (
		<div className="flex flex-col gap-5">
			<Link to="/espace/offres" className="t-label text-mg-action hover:underline">
				← Mes offres
			</Link>

			<Card>
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="min-w-0">
						<h1 className="t-h2 text-mg-heading">{request.job}</h1>
						<p className="t-body mt-1 text-mg-secondary">
							Déposée le {formatDate(request.createdAt)}
							{request.duplicatedFrom ? " · redéposée depuis une offre précédente" : ""}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Badge tone={status?.tone ?? "neutral"}>{status?.label ?? request.status}</Badge>
						{/*
						 * Le redépôt n'est proposé que sur une offre TERMINÉE. Sur une offre
						 * en ligne il créerait un doublon que l'école devrait trier ; sur une
						 * offre en attente il doublerait une demande déjà sur son bureau.
						 */}
						{request.status === "archivee" || request.status === "refusee" ? (
							<Button tone="secondary" size="sm" disabled={busy} onClick={redeposer}>
								{busy ? "Redépôt…" : "Redéposer"}
							</Button>
						) : null}
					</div>
				</div>

				{echec ? (
					<p
						role="alert"
						className="t-body mt-4 rounded-field bg-mg-error-subtle px-4 py-3 text-mg-error-fg"
					>
						Le redépôt n'a pas abouti. Réessayez dans un instant, ou écrivez à votre école.
					</p>
				) : null}

				{request.schoolNote ? (
					<p className="t-body mt-4 rounded-field bg-mg-warning-subtle px-4 py-3 text-mg-heading">
						<span className="font-semibold">{request.schoolName} :</span> {request.schoolNote}
					</p>
				) : null}

				<dl className="mt-5 grid gap-x-6 gap-y-3 border-t border-mg-border-subtle pt-5 sm:grid-cols-2">
					{facts
						.filter(([, value]) => Boolean(value))
						.map(([label, value]) => (
							<div key={label}>
								<dt className="t-caption text-mg-secondary">{label}</dt>
								<dd className="t-body-md mt-0.5 text-mg-heading">{value}</dd>
							</div>
						))}
				</dl>

				{request.description ? (
					<div className="mt-5 border-t border-mg-border-subtle pt-5">
						<p className="t-caption text-mg-secondary">Missions et profil recherché</p>
						<p className="t-body mt-1.5 whitespace-pre-line text-mg-body">{request.description}</p>
					</div>
				) : null}
			</Card>

			{selections.length > 0 ? (
				<Card padded={false}>
					<CardHeader title="Les profils reçus pour cette offre" />
					<ul>
						{selections.map((selection) => (
							<li key={selection.id} className="border-b border-mg-border-subtle last:border-b-0">
								<Link
									to={`/espace/profils/${selection.id}`}
									className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-mg-hover"
								>
									<div className="min-w-0">
										<p className="t-body-md truncate text-mg-heading">{selection.title}</p>
										<p className="t-caption mt-0.5 text-mg-secondary">
											{selection.profiles.length} profils · reçu {relativeDate(selection.createdAt)}
										</p>
									</div>
									<Badge tone="brand">Voir</Badge>
								</Link>
							</li>
						))}
					</ul>
				</Card>
			) : null}
		</div>
	);
}
